/**
 * Monsters, their shots, the Baron and level 100's boss (spec 02 §5, §6, §7.5, §9.4, §9.5) - owned
 * by the MONSTER stream. The world calls `spawnMonsters`, `updateMonsters` (I-ticks) and
 * `updateProjectiles` (M-ticks); the flow calls `angerAll` / `spawnBarons`, the PLAYER stream
 * `calmAll`, the BUBBLE stream's bolts `hitBossAt`. Ported from the rebb64 disassembly; see each file
 * for the routines.
 *
 * Positions are VIC sprite registers (types.ts). For the renderer:
 * - Monsters: `monsterPose(m)` names the movement ('frozen' under the power heart, 'attack' while one
 *   winds up to throw); `m.ai.frame` is the C64 sprite frame (its walk cycle, 0..faceFrames-1 facing
 *   right, the next group facing left) and `m.facing` follows it; `monsterColour(m)` the C64 colour
 *   (kind, light red angry, light blue frozen).
 * - Shots (`world.projectiles`): `projectileLook(p)` - the 16x16 top-left (VIC frame, as a bubble's),
 *   the C64 picture, the burst. The laser is one 16x16 cell falling a row a pass, not a beam.
 * - The boss (`world.boss`): `bossLook(world)` - position, visibility (hidden before READY goes, while
 *   blinking late in a stun, once beaten), stunned picture, facing, colour.
 */

import { angerAllMonsters, calmAllMonsters } from './anger';
import { spawnBaronsNow } from './baron';
import { boltHitsBoss, bossMTick } from './boss';
import { projectilesPass } from './projectiles';
import { spawnLevelMonsters } from './spawn';
import { monstersITick } from './update';
import type { World } from '../types';

export { monsterColour, monsterPose, type MonsterPose } from './pose';
export { projectileLook, type ProjectileLook } from './projectiles';
export { bossLook, type BossLook } from './boss';
/**
 * The Barons' main-loop passes (02 §6: they move on M-ticks). `updateMonsters` already catches them
 * up on every I-tick; the world may also call this on M-tick frames for exact timing (idempotent).
 */
export { updateBarons } from './baron';

/** Create the level's monsters from its data, ready to drop in (02 §5 Spawning); on level 100 the boss. */
export function spawnMonsters(world: World): void {
  spawnLevelMonsters(world);
}

/**
 * One I-tick of every monster: drop-in, waiting, AI and movement (attacks included), the power heart's
 * freeze, contact with players (or level 100's boss); then the Barons, for each main-loop pass since
 * they last ran. While play is frozen only the drop-in moves; under the clock the drop-in and the
 * heart-frozen move, the contact test runs.
 */
export function updateMonsters(world: World): void {
  monstersITick(world);
}

/** HURRY UP (02 §6): every monster out of a bubble turns angry. */
export function angerAll(world: World): void {
  angerAllMonsters(world);
}

/** A player died with two or more monsters left: all calm down (02 §4.1). Counts the monsters itself. */
export function calmAll(world: World): void {
  calmAllMonsters(world);
}

/**
 * One main-loop pass of the monsters' shots (02 §5 attacks): each moves, meets walls, and kills a
 * player it touches (not under the power heart); under the clock they stand; at the level clear they
 * burst. Then the boss's stun timer, on level 100.
 */
export function updateProjectiles(world: World): void {
  projectilesPass(world);
  bossMTick(world);
}

/**
 * A bolt at (x, y) (a 16x16 box's top-left, VIC frame: 8col + $14, 8row + $15) on level 100: if it is
 * in the boss's box (-8..63 px across, -8..55 down of `boss.x/y`) and the boss is fighting, the boss
 * loses one of its bolts left (`boss.hp`: 51, then 41 after a stun) and the bolt is used up - the caller
 * should end it (the C64 turns it into type $3A in collect_item, $1134). True if it hit.
 */
export function hitBossAt(world: World, x: number, y: number): boolean {
  return boltHitsBoss(world, x, y);
}

/** Ten seconds after HURRY UP: one Baron per player in play, from a random corner (02 §6). */
export function spawnBarons(world: World): void {
  spawnBaronsNow(world);
}
